import type { DraftJsContent, DraftJsBlock } from './Common'

export const isDraftJsContent = (value: unknown): value is DraftJsContent => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'blocks' in value &&
    Array.isArray((value as DraftJsContent).blocks) &&
    'entityMap' in value
  )
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

interface InlineStyleRange {
  offset?: number
  length?: number
  style?: string
}

function applyBoldRanges(text: string, ranges: InlineStyleRange[]): string {
  if (!Array.isArray(ranges) || !ranges.length) return escapeHtml(text)

  const boldRanges = ranges
    .filter((r) => r && r.style === 'BOLD' && Number.isFinite(r.offset) && Number.isFinite(r.length))
    .map((r) => ({ offset: Math.max(0, r.offset ?? 0), length: Math.max(0, r.length ?? 0) }))
    .filter((r) => r.length > 0)
    .sort((a, b) => a.offset - b.offset)

  if (!boldRanges.length) return escapeHtml(text)

  let out = ''
  let cursor = 0

  for (const r of boldRanges) {
    const start = Math.min(Math.max(r.offset, 0), text.length)
    const end = Math.min(Math.max(r.offset + r.length, 0), text.length)
    if (end <= start) continue

    out += escapeHtml(text.slice(cursor, start))
    out += `<strong>${escapeHtml(text.slice(start, end))}</strong>`
    cursor = end
  }

  out += escapeHtml(text.slice(cursor))
  return out
}

function renderBlockToHtml(block: DraftJsBlock): string {
  const text = typeof block.text === 'string' ? block.text : ''
  const ranges = (block as DraftJsBlock & { inlineStyleRanges?: InlineStyleRange[] }).inlineStyleRanges ?? []
  const styled = applyBoldRanges(text, Array.isArray(ranges) ? ranges : [])

  switch (block.type) {
    case 'header-one':
      return `<h1>${styled}</h1>`
    case 'header-two':
      return `<h2>${styled}</h2>`
    case 'header-three':
      return `<h3>${styled}</h3>`
    case 'unordered-list-item':
    case 'ordered-list-item':
      return styled
    default:
      if (!text.trim()) return '<p></p>'
      return `<p>${styled}</p>`
  }
}

export function draftJsContentToHtml(content: DraftJsContent): string {
  const blocks = Array.isArray(content.blocks) ? content.blocks : []

  let html = ''
  let i = 0

  while (i < blocks.length) {
    const block = blocks[i]

    if (block.type === 'unordered-list-item') {
      const items: string[] = []
      while (i < blocks.length && blocks[i].type === 'unordered-list-item') {
        items.push(
          `<li>${renderBlockToHtml(blocks[i])}</li>`.replace(/^<li><p>/, '<li>').replace(/<\/p><\/li>$/, '</li>')
        )
        i += 1
      }
      html += `<ul>${items.join('')}</ul>`
      continue
    }

    if (block.type === 'ordered-list-item') {
      const items: string[] = []
      while (i < blocks.length && blocks[i].type === 'ordered-list-item') {
        items.push(
          `<li>${renderBlockToHtml(blocks[i])}</li>`.replace(/^<li><p>/, '<li>').replace(/<\/p><\/li>$/, '</li>')
        )
        i += 1
      }
      html += `<ol>${items.join('')}</ol>`
      continue
    }

    html += renderBlockToHtml(block)
    i += 1
  }

  return html
}

export function draftJsContentToPlainText(content: DraftJsContent): string {
  const blocks = Array.isArray(content.blocks) ? content.blocks : []
  return blocks
    .map((b) => {
      const t = typeof b.text === 'string' ? b.text : ''
      if (b.type === 'unordered-list-item') return `• ${t}`
      if (b.type === 'ordered-list-item') return `- ${t}`
      return t
    })
    .join('\n')
    .trim()
}
